import { useState, useEffect, useMemo } from 'react'
import { useToast } from '../context/ToastContext'
import { getTillRecord, getAllTillRecords, saveTillRecord, deleteTillRecord } from '../lib/store'
import DateInput from '../components/ui/DateInput'
import DailyEntry from '../components/till/DailyEntry'
import TillHistory from '../components/till/TillHistory'
import TillAnalytics from '../components/till/TillAnalytics'
import CashOutLog from '../components/till/CashOutLog'
import { Wallet, ClipboardList, BarChart3, Receipt } from 'lucide-react'

const TABS = [
  { id: 'entry', label: 'Daily Entry', icon: Wallet },
  { id: 'history', label: 'History', icon: ClipboardList },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'cashout', label: 'Cash Out Log', icon: Receipt },
]

const emptyRow = () => ({ description: '', amount: '', type: 'Expense' })

function getVarianceStatus(variance) {
  const abs = Math.abs(variance)
  if (abs <= 50) return 'BALANCED'
  if (abs <= 200) return 'MINOR_VARIANCE'
  return 'SHORTAGE'
}

export default function TillManagement() {
  const { addToast } = useToast()
  const today = new Date().toISOString().split('T')[0]

  const [tab, setTab] = useState('entry')
  const [date, setDate] = useState(today)
  const [sales, setSales] = useState({ kitchen: 0, bakery: 0, tuckShop: 0, grandTotal: 0 })
  const [cashOutItems, setCashOutItems] = useState([emptyRow()])
  const [actualCash, setActualCash] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)

  const allRecords = useMemo(() => getAllTillRecords(), [refreshKey])

  // Load existing record when date changes
  useEffect(() => {
    const existing = getTillRecord(date)
    if (existing) {
      setSales({
        kitchen: existing.kitchen || 0,
        bakery: existing.bakery || 0,
        tuckShop: existing.tuckShop || 0,
        grandTotal: existing.grandTotal || 0,
      })
      setCashOutItems(existing.cashOutItems?.length ? existing.cashOutItems : [emptyRow()])
      setActualCash(existing.actualCash ?? '')
    } else {
      setSales({ kitchen: 0, bakery: 0, tuckShop: 0, grandTotal: 0 })
      setCashOutItems([emptyRow()])
      setActualCash('')
    }
  }, [date])

  const existingRecord = getTillRecord(date)

  const handleSave = () => {
    if (!sales.grandTotal && sales.grandTotal !== 0) {
      addToast('Enter sales data before saving', 'warning')
      return
    }

    const validCashOut = cashOutItems.filter(r => r.description || Number(r.amount) > 0)
    const totalCashOut = validCashOut.reduce((s, r) => s + (Number(r.amount) || 0), 0)
    const totalSales = Number(sales.grandTotal) || 0
    const expectedTill = totalSales - totalCashOut
    const variance = expectedTill - (Number(actualCash) || 0)

    const record = {
      date,
      kitchen: Number(sales.kitchen) || 0,
      bakery: Number(sales.bakery) || 0,
      tuckShop: Number(sales.tuckShop) || 0,
      grandTotal: totalSales,
      cashOutItems: validCashOut.map(r => ({
        description: r.description,
        amount: Number(r.amount) || 0,
        type: r.type,
      })),
      actualCash: Number(actualCash) || 0,
      expectedTill,
      variance,
      status: getVarianceStatus(variance),
    }

    saveTillRecord(record)
    setRefreshKey(k => k + 1)
    addToast(existingRecord ? 'Till record updated' : 'Till record saved', 'success')
  }

  const handleDelete = (dateToDelete) => {
    if (!confirm(`Delete till record for ${dateToDelete}?`)) return
    deleteTillRecord(dateToDelete)
    setRefreshKey(k => k + 1)
    addToast('Till record deleted', 'success')
    if (dateToDelete === date) {
      setSales({ kitchen: 0, bakery: 0, tuckShop: 0, grandTotal: 0 })
      setCashOutItems([emptyRow()])
      setActualCash('')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Wallet size={20} className="text-brand-400" />
            Till & Cash Management
          </h1>
          <p className="text-xs text-gray-500 mt-1">Reconcile daily POS sales, cash-outs, and till balance</p>
        </div>
        {tab === 'entry' && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Date:</span>
            <DateInput value={date} onChange={setDate} />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-800 pb-px">
        {TABS.map(t => {
          const Icon = t.icon
          const active = tab === t.id
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                active
                  ? 'bg-gray-900 text-white border-b-2 border-brand-500'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-900/50'
              }`}
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      {tab === 'entry' && (
        <DailyEntry
          sales={sales}
          setSales={setSales}
          cashOutItems={cashOutItems}
          setCashOutItems={setCashOutItems}
          actualCash={actualCash}
          setActualCash={setActualCash}
          onSave={handleSave}
          existingRecord={existingRecord}
        />
      )}

      {tab === 'history' && (
        <TillHistory records={allRecords} onDelete={handleDelete} />
      )}

      {tab === 'analytics' && (
        <TillAnalytics records={allRecords} />
      )}

      {tab === 'cashout' && (
        <CashOutLog records={allRecords} />
      )}
    </div>
  )
}
